/**
 * Industry page content, supplied by the content writer. Use this text as written: do not add claims,
 * figures or programme details that are not here.
 *
 * Each industry page reads one entry from INDUSTRY_CONTENT (keyed by the industry id in industries.js).
 * These writer pages are the published industry landing pages.
 *
 * `programmes.cards[].slug` identifies the programme; cards link to the live page on alearninglab.com (see getProgramSourceUrl).
 */

/** Text that is identical on every writer page. */
const SHARED = {
  facts: ["Ages 13 to 18", "No prior experience needed"],
  paths: {
    title: "Pick a path. See where it goes.",
    lead: "Tap a field to see careers, the route there, and typical earning potential.",
    careersLabel: "Careers you could pick",
    routeLabel: "The route there",
    payLabel: "Typical earning potential: ",
    payNote: "Indicative only. Actual pay varies by role, employer, location and experience.",
  },
  journey: {
    title: "From curiosity to career",
    lead: "You don't need it all figured out at 14. You just need to start testing it.",
    sample: { age: "Ages 13 to 15", title: "Sample the work", text: "Spend time with professionals and see a typical day, including the tough parts." },
    story: { age: "Ages 16 to 18", title: "Build your story", text: "Projects, internships and achievements show universities why you care." },
    subjectsAge: "Ages 15 to 16",
    subjectsTitle: "Shape your subjects",
    afterAge: "After school",
    afterTitle: "Study and specialise",
  },
  programmes: {
    title: "Start with a virtual internship",
    lead: "Live, hands-on internships guided by industry experts. Pick the one that sounds like you.",
    link: "View programme",
  },
  cta: {
    title: "Ready to find your fit?",
    text: "Talk to us and we'll help you pick the right programme.",
    label: "Get in touch",
  },
};

function journey(subjectsText, afterText) {
  const j = SHARED.journey;
  return {
    title: j.title,
    lead: j.lead,
    steps: [
      j.sample,
      { age: j.subjectsAge, title: j.subjectsTitle, text: subjectsText },
      j.story,
      { age: j.afterAge, title: j.afterTitle, text: afterText },
    ],
  };
}

export const INDUSTRY_CONTENT = {
  health: {
    crumb: "Healthcare and human sciences",
    h1: "Step into the world of healthcare",
    h1Highlight: "healthcare",
    sub: "Explore clinical practice and human behaviour through experiences alongside doctors, dentists and therapists.",
    facts: SHARED.facts,
    intro: {
      title: "Healthcare isn't one job. It's a whole world.",
      lead: "Ask most teens to name a healthcare career and you'll hear \"doctor\". The full list is far longer, and growing.",
      stats: [
        { big: "10M", text: "projected global shortage of health workers by 2030 (WHO)" },
        { big: "1 in 8", text: "people worldwide live with a mental health condition (WHO)" },
        { big: "4 fields", text: "medicine, psychology, healthtech and dentistry, all in one industry" },
      ],
      areas: [
        { title: "Clinical care", text: "Diagnosing, treating and healing." },
        { title: "Mind and behaviour", text: "Mental health is now part of mainstream healthcare." },
        { title: "Digital health", text: "Telemedicine, health data and AI diagnostics are creating roles that didn't exist ten years ago." },
        { title: "Research", text: "Clinical trials and new treatments need people who ask good questions." },
      ],
    },
    paths: {
      ...SHARED.paths,
      tabs: [
        {
          name: "Medicine",
          roles: ["Doctor", "Surgeon", "Radiologist", "Public health specialist", "Clinical researcher"],
          route: [
            "Study biology, chemistry and physics in your final school years",
            "Complete medical school (entry tests vary by country)",
            "Train as a specialist through residency or postgraduate study",
          ],
          pay: "In India, early-career doctors may earn up to about ₹15 lakh a year, with experienced specialists reaching up to ₹1 crore+ in some settings. In markets like the US, physicians can earn up to $200K+ depending on specialty and experience.",
        },
        {
          name: "Psychology",
          roles: ["Clinical psychologist", "Counsellor", "Educational psychologist", "Sports psychologist", "Organisational psychologist", "Forensic psychologist"],
          route: [
            "Take psychology, biology or social sciences if your school offers them",
            "Earn a bachelor's, then a master's or doctorate in psychology",
            "Complete supervised clinical training and get licensed where you practise",
          ],
          pay: "In India, psychologists with experience may earn up to about ₹20 lakh a year. In the US, median earnings can reach up to about $90K, with specialists and private practitioners often higher.",
        },
        {
          name: "Healthtech",
          roles: ["Health informatics analyst", "Medical software developer", "Clinical research associate", "Telemedicine specialist", "AI diagnostics roles"],
          route: [
            "Pair biology with maths or computer science at school",
            "Study health sciences, biomedical engineering, computing or data science",
            "Add specialist skills in health informatics, clinical research or AI",
          ],
          pay: "In India, healthtech roles may earn up to about ₹25 lakh+ a year with seniority. Global tech and biotech hubs can offer higher compensation, depending on role and employer.",
        },
        {
          name: "Dentistry",
          roles: ["General dentist", "Orthodontist", "Oral surgeon", "Cosmetic dentist", "Dental researcher"],
          route: [
            "Study biology, chemistry and physics in your final school years",
            "Complete a dental degree (entry tests vary by country)",
            "Specialise in areas like orthodontics or oral surgery",
          ],
          pay: "In the US, dentists may earn up to $150K+ a year in some settings. In India, private practice income can grow substantially with reputation and experience.",
        },
      ],
    },
    journey: journey(
      "Choose sciences that keep medicine, dentistry and psychology pathways open.",
      "Complete your degree, then specialise. In healthcare, this is where careers and pay grow most.",
    ),
    programmes: {
      ...SHARED.programmes,
      cards: [
        { title: "Medicine and Healthcare", text: "Follow the work of doctors and see how care is delivered.", slug: "medicine-and-healthcare-virtual-internship" },
        { title: "Psychology and Human Behaviour", text: "Understand how people think, feel and act.", slug: "psychology-and-human-behaviour-virtual-internship" },
        { title: "Healthtech", text: "See how technology is changing the way we heal.", slug: "healthtech-virtual-internship" },
        { title: "Dentistry", text: "Explore oral health, from check-ups to specialist care.", slug: "dentistry-virtual-internship" },
      ],
    },
    cta: SHARED.cta,
  },

  "ui-ux-creative-designing": {
    crumb: "UI/UX and creative designing",
    h1: "Make ideas impossible to ignore",
    h1Highlight: "impossible to ignore",
    sub: "Design digital interfaces and develop creative solutions to real-world challenges.",
    facts: SHARED.facts,
    intro: {
      title: "Design is how ideas become unforgettable.",
      lead: "Every app you open and every building you walk into started on a designer's screen.",
      stats: [
        { big: "8%", text: "projected growth for web developers and digital designers in the US, 2023 to 2033 (BLS)" },
        { big: "Top skill", text: "creative thinking is among the fastest-rising skills employers want (WEF, 2025)" },
        { big: "2 worlds", text: "digital and physical, united by one design mindset" },
      ],
      areas: [
        { title: "Product design", text: "Shaping how apps and tools feel to use." },
        { title: "User research", text: "Understanding people before designing for them." },
        { title: "Architecture", text: "Designing buildings and spaces." },
        { title: "Sustainable design", text: "Making beautiful things that last." },
      ],
    },
    paths: {
      ...SHARED.paths,
      tabs: [
        {
          name: "UI/UX Product Design",
          roles: ["UX designer", "UI designer", "Product designer", "UX researcher", "Design strategist"],
          route: [
            "Mix art, design and technology, and start a portfolio now",
            "Study design or human-computer interaction, or build skills through projects and courses",
            "Specialise in research, interaction design or product strategy",
          ],
          pay: "In India, product design roles may earn up to about ₹30 lakh+ a year. At global tech firms, compensation can reach up to $100K+ in senior roles.",
        },
        {
          name: "Architecture and Design",
          roles: ["Architect", "Sustainable design consultant", "Urban planner", "Interior designer", "Landscape architect"],
          route: [
            "Take maths, physics and art or design in school",
            "Earn an architecture degree accredited where you plan to practise",
            "Complete supervised experience, then professional licensure",
          ],
          pay: "In India, architects may earn up to about ₹25 lakh+ a year. In the US, median earnings can reach up to about $93K, with studio leaders and founders often higher.",
        },
      ],
    },
    journey: journey(
      "Mix art and design with maths, technology or physics, depending on your path.",
      "Study design or architecture, specialise, and keep your portfolio growing.",
    ),
    programmes: {
      ...SHARED.programmes,
      cards: [
        { title: "UI/UX Product Design", text: "Design digital products people enjoy using.", slug: "ui-ux-product-design-virtual-internship" },
        { title: "Architecture and Design", text: "Explore architecture with sustainability at its core.", slug: "architecture-and-design-virtual-internship" },
      ],
    },
    cta: SHARED.cta,
  },

  "gaming-innovation": {
    crumb: "Gaming and innovation",
    h1: "Play with ideas. Build what's next.",
    h1Highlight: "Build what's next.",
    sub: "Explore game development, virtual reality and emerging technologies through hands-on projects.",
    facts: SHARED.facts,
    intro: {
      title: "Where play and precision become careers.",
      lead: "Games and aviation look worlds apart. Both reward the same things: curiosity, skill and nerve.",
      stats: [
        { big: "$180B+", text: "global games industry, bigger than film and music combined in revenue" },
        { big: "3B+", text: "gamers worldwide" },
        { big: "674,000", text: "new airline pilots needed over the next 20 years (Boeing outlook)" },
      ],
      areas: [
        { title: "Game design and art", text: "Worlds, characters and mechanics." },
        { title: "Immersive tech", text: "VR and AR experiences." },
        { title: "Flight operations", text: "Piloting, air traffic and airline operations." },
        { title: "Aerospace innovation", text: "The machines that take us higher." },
      ],
    },
    paths: {
      ...SHARED.paths,
      tabs: [
        {
          name: "Gaming and VR",
          roles: ["Game designer", "Game programmer", "3D artist", "VR and AR developer", "Technical artist"],
          route: [
            "Maths, computing and art all help. Start building small games now",
            "Study game design, computer science or digital arts, or build a strong portfolio of your own games",
            "Join a studio or release your own projects",
          ],
          pay: "In India, game development roles may earn up to about ₹30 lakh+ a year. Abroad, developers can earn up to $80K+, with senior studio roles often higher.",
        },
        {
          name: "Aviation",
          roles: ["Airline pilot", "Air traffic controller", "Aerospace engineer", "Aviation manager", "Flight operations specialist"],
          route: [
            "Focus on maths, physics and strong English",
            "Complete flight training or an aviation or aerospace degree (licensing varies by country)",
            "Build flying hours and ratings, or specialise on the ground",
          ],
          pay: "Experienced airline captains in India may earn up to about ₹50 lakh+ a year. In the US, airline pilots can reach up to $200K+ at the median in some markets.",
        },
      ],
    },
    journey: journey(
      "Choose maths and physics, with computing or art depending on your path.",
      "Train, build experience, and specialise: a studio, a cockpit or your own start-up.",
    ),
    programmes: {
      ...SHARED.programmes,
      cards: [
        { title: "Gaming and VR", text: "Learn how games and virtual worlds are built.", slug: "gaming-and-vr-virtual-internship" },
        { title: "Aviation", text: "Discover life on and around the flight deck.", slug: "aviation-virtual-internship" },
      ],
    },
    cta: SHARED.cta,
  },

  finance: {
    crumb: "Finance, investment and FinTech",
    h1: "Put your financial thinking to work",
    h1Highlight: "to work",
    sub: "Investigate investments, decode financial trends and discover how technology is reshaping the world of finance.",
    facts: SHARED.facts,
    intro: {
      title: "Every big decision runs on money.",
      lead: "From a start-up's first cheque to a payment in a split second, finance is where ideas get fuel.",
      stats: [
        { big: "#2", text: "FinTech engineer is the second-fastest-growing role worldwide (WEF, 2025)" },
        { big: "9%", text: "projected growth for financial and investment analysts in the US, 2023 to 2033 (BLS)" },
        { big: "15B+", text: "UPI payments in India every month, and rising" },
      ],
      areas: [
        { title: "Investing", text: "Growing money in markets and companies." },
        { title: "Venture capital", text: "Backing the start-ups of tomorrow." },
        { title: "Digital payments", text: "The tech behind every tap and transfer." },
        { title: "Risk and analysis", text: "Reading data to make better calls." },
      ],
    },
    paths: {
      ...SHARED.paths,
      tabs: [
        {
          name: "Finance and Venture Capital",
          roles: ["Investment analyst", "Venture capital associate", "Financial planner", "Portfolio manager", "Investment banker"],
          route: [
            "Strengthen maths and economics, and follow the markets",
            "Study finance, economics, accounting or business",
            "Earn professional credentials such as the CFA and build experience in analysis or deals",
          ],
          pay: "In India, analysts and investment roles may earn up to about ₹50 lakh+ a year. In the US, financial analysts can reach up to about $100K at the median, with senior investment roles often much higher.",
        },
        {
          name: "FinTech",
          roles: ["FinTech product manager", "Payments engineer", "Risk analyst", "Data analyst", "Blockchain developer"],
          route: [
            "Pair maths with computer science at school",
            "Study finance, computing or data science",
            "Specialise in payments, lending, security or regulatory technology",
          ],
          pay: "In India, FinTech roles may earn up to about ₹40 lakh+ a year. Global firms can offer compensation up to six figures in USD, depending on role and level.",
        },
      ],
    },
    journey: journey(
      "Strengthen maths and economics, and add computer science if you like FinTech.",
      "Study finance, economics or computing, then earn professional credentials.",
    ),
    programmes: {
      ...SHARED.programmes,
      cards: [
        { title: "Finance and Venture Capital", text: "See how investors spot and back great ideas.", slug: "finance-and-venture-capital-virtual-internship" },
        { title: "FinTech", text: "Explore the technology reshaping money.", slug: "fintech-virtual-internship" },
      ],
    },
    cta: SHARED.cta,
  },

  "business-marketing": {
    crumb: "Business and marketing",
    h1: "Turn big ideas into business.",
    h1Highlight: "into business.",
    sub: "Develop business concepts, shape brand strategies and explore how ideas find their market.",
    facts: SHARED.facts,
    intro: {
      title: "Every brand began with someone's idea.",
      lead: "The companies you love were once a sketch, a spark and a very brave first customer.",
      stats: [
        { big: "8%", text: "projected growth for marketing managers in the US, 2023 to 2033 (BLS)" },
        { big: "Leadership", text: "and social influence are among the fastest-rising skills worldwide (WEF, 2025)" },
        { big: "Day 1", text: "no permission needed. Start with a problem worth solving" },
      ],
      areas: [
        { title: "Entrepreneurship", text: "Starting and scaling ventures." },
        { title: "Brand strategy", text: "Making people care about what you make." },
        { title: "Digital marketing", text: "Reaching audiences online." },
        { title: "Innovation", text: "Finding better ways to do things." },
      ],
    },
    paths: {
      ...SHARED.paths,
      tabs: [
        {
          name: "Entrepreneurship and Innovation",
          roles: ["Founder", "Product manager", "Business developer", "Innovation consultant", "Social entrepreneur"],
          route: [
            "Start something small: a club, a product or a service",
            "Study business, economics, or the subject you want to build a business around",
            "Gain experience in start-ups, then launch or lead",
          ],
          pay: "Founders' outcomes vary widely. Product and business leaders in India may earn up to about ₹30 lakh+ a year, and equity in successful start-ups can add further upside.",
        },
        {
          name: "Marketing and Brand Strategy",
          roles: ["Brand strategist", "Marketing manager", "Content strategist", "Growth marketer", "Market researcher"],
          route: [
            "Build communication, maths and psychology skills",
            "Study marketing, business or communications",
            "Build a track record in campaigns and analytics, then specialise",
          ],
          pay: "In India, marketing roles may earn up to about ₹35 lakh+ a year. In the US, marketing managers can reach up to $150K+ at the median in some industries.",
        },
      ],
    },
    journey: journey(
      "Choose a mix of maths, economics and communication-rich subjects.",
      "Study business, marketing or any field you'd love to build in, then launch or lead.",
    ),
    programmes: {
      ...SHARED.programmes,
      cards: [
        { title: "Entrepreneurship and Innovation", text: "Turn an idea into a business plan.", slug: "entrepreneurship-and-innovation-virtual-internship" },
        { title: "Marketing and Brand Strategy", text: "Learn how brands win attention and loyalty.", slug: "marketing-and-brand-strategy-virtual-internship" },
      ],
    },
    cta: SHARED.cta,
  },

  "law-public-policy-global-affairs": {
    crumb: "Law, public policy and global affairs",
    h1: "Experience law in action.",
    h1Highlight: "in action.",
    sub: "Step into legal practice through case analysis, courtroom simulations and persuasive argumentation.",
    facts: SHARED.facts,
    intro: {
      title: "Law shapes how the world works.",
      lead: "Every right you have, every contract you sign and every treaty between nations was written by someone.",
      stats: [
        { big: "8%", text: "projected growth for lawyers in the US, 2023 to 2033 (BLS)" },
        { big: "#1 skill", text: "analytical thinking is the most in-demand skill worldwide (WEF, 2025)" },
        { big: "4 arenas", text: "courts, companies, governments and global bodies" },
      ],
      areas: [
        { title: "Litigation", text: "Arguing cases and protecting rights." },
        { title: "Corporate law", text: "Advising businesses and shaping deals." },
        { title: "Public policy", text: "Turning ideas into laws and programmes." },
        { title: "Global affairs", text: "Diplomacy, human rights and international cooperation." },
      ],
    },
    paths: {
      ...SHARED.paths,
      tabs: [
        {
          name: "Lawyer",
          roles: ["Litigator", "Corporate lawyer", "Human rights lawyer", "In-house counsel", "Legal adviser"],
          route: [
            "Read widely, write well and join debate or mock trial",
            "Earn a law degree (in some countries, a first degree in any subject, then law school)",
            "Qualify through the bar or equivalent exams and build practice experience",
          ],
          pay: "Top firms in India may offer starting packages up to about ₹30 lakh+ a year for newcomers. In the US, lawyers can earn up to about $145K at the median, with partners often far higher.",
        },
        {
          name: "Public policy",
          roles: ["Policy analyst", "Legislative adviser", "Think tank researcher", "Civil servant", "Regulator"],
          route: [
            "Follow current affairs and study history, economics and civics",
            "Study public policy, political science, economics or law",
            "Build experience in government, research or advocacy",
          ],
          pay: "Policy and regulatory roles in India may earn up to about ₹15 lakh a year early on, with growth into senior advisory posts.",
        },
        {
          name: "Global affairs",
          roles: ["Diplomat", "International relations analyst", "UN or NGO programme officer", "Political risk analyst", "Foreign affairs journalist"],
          route: [
            "Learn languages and follow world events",
            "Study international relations, law, economics or political science",
            "Gain experience through international organisations, think tanks or the foreign service",
          ],
          pay: "Senior roles in international organisations and consultancies may earn up to $100K+ a year, depending on organisation and location.",
        },
      ],
    },
    journey: journey(
      "Build reading, writing and debating skills, with history, economics and languages.",
      "Study law or a related field, qualify to practise, then choose your arena.",
    ),
    programmes: {
      ...SHARED.programmes,
      cards: [
        { title: "Lawyer", text: "Step into the working day of a lawyer, from case prep to the courtroom.", slug: "lawyer-virtual-internship" },
      ],
    },
    cta: SHARED.cta,
  },

  "technology-and-engineering": {
    crumb: "Technology and engineering",
    h1: "Explore the technology shaping tomorrow",
    h1Highlight: "shaping tomorrow",
    sub: "Work with emerging technologies, investigate new ideas and explore how innovation solves real-world problems.",
    facts: SHARED.facts,
    intro: {
      title: "Everything you use was built by someone.",
      lead: "Apps, bridges, rockets, robots. Behind each one is a team that started as curious students.",
      stats: [
        { big: "170M", text: "new jobs projected worldwide by 2030, with tech roles leading (WEF, 2025)" },
        { big: "17%", text: "projected growth in US software developer jobs, 2023 to 2033 (BLS)" },
        { big: "10+", text: "engineering branches, from civil and mechanical to aerospace and biomedical" },
      ],
      areas: [
        { title: "Software and AI", text: "Programs, platforms and intelligent systems." },
        { title: "Hardware and electronics", text: "Chips, sensors and the devices around you." },
        { title: "Infrastructure and energy", text: "Cities, grids and a cleaner planet." },
        { title: "Machines and mobility", text: "Vehicles, robots and automation." },
      ],
    },
    paths: {
      ...SHARED.paths,
      tabs: [
        {
          name: "AI and Computer Science",
          roles: ["Machine learning engineer", "Software developer", "Data scientist", "Cybersecurity analyst", "Robotics software engineer"],
          route: [
            "Build strong maths skills and learn to code early",
            "Study computer science, AI, data science or a related degree",
            "Specialise, and grow a portfolio of projects alongside your studies",
          ],
          pay: "In India, software and AI roles may earn up to about ₹40 lakh+ a year. In the US, software developers can reach up to about $130K at the median, with AI specialists often higher.",
        },
        {
          name: "Engineering",
          roles: ["Civil engineer", "Mechanical engineer", "Electrical engineer", "Aerospace engineer", "Biomedical engineer", "Renewable energy engineer"],
          route: [
            "Focus on maths and physics in your final school years",
            "Earn an accredited engineering degree in your chosen branch",
            "Gain professional experience and, where relevant, professional licensure",
          ],
          pay: "In India, engineering roles may earn up to about ₹25 lakh+ a year. In the US, engineers can reach up to about $90K+ at the median, with specialised fields like aerospace and energy often higher.",
        },
      ],
    },
    journey: journey(
      "Prioritise maths and physics, and computer science if it's offered.",
      "Choose a branch or specialism, and keep building. Employers love a portfolio.",
    ),
    programmes: {
      ...SHARED.programmes,
      cards: [
        {
          title: "AI and Computer Science",
          text: "Explore how intelligent systems are designed and built.",
          slug: "ai-and-computer-science-virtual-internship",
          duration: "6 Weeks",
          ages: "13-18",
          sessions: 6,
        },
        {
          title: "Engineering",
          text: "See how engineers turn problems into working solutions.",
          slug: "engineering-virtual-internship",
          duration: "6 Weeks",
          ages: "13-18",
          sessions: 6,
        },
      ],
    },
    cta: SHARED.cta,
  },
};

export function getIndustryContent(id) {
  return INDUSTRY_CONTENT[id] ?? null;
}
